import {
  generateBindings,
  observableValue,
  useSharedState,
  view,
  View,
} from '@esui/core';
import { spread } from '@esui/core/utils/spread.js';
import '@material/mwc-icon-button-toggle';
import { html, SVGTemplateResult, TemplateResult } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';

export { IconButtonToggle as MdcIconButtonToggle } from '@material/mwc-icon-button-toggle';

class Props {
  onIcon?: string | View | TemplateResult | SVGTemplateResult = '';
  offIcon?: string | View | TemplateResult | SVGTemplateResult = '';
  on = observableValue(false);
  disabled = false;
  ariaLabelOn?: string = undefined;
  ariaLabelOff?: string = undefined;
  ariaLabel = '';
  content: (View | TemplateResult | SVGTemplateResult)[] = [];
}

function iconToAttr(icon?: string | View | TemplateResult | SVGTemplateResult) {
  if (typeof icon === 'string') {
    return icon;
  }
  return undefined;
}

function iconToSlotted(
  icon?: string | View | TemplateResult | SVGTemplateResult
) {
  if (icon && typeof icon !== 'string') {
    return icon instanceof View ? icon.body : icon;
  }
  return undefined;
}

export const [IconButtonToggle] = view('esui-mwc-icon-button-toggle', {
  Props,

  mapBuilder:
    IconButtonToggleBuilder =>
    (...content: (View | TemplateResult | SVGTemplateResult)[]) =>
      IconButtonToggleBuilder().content(content),

  template(props) {
    const on = useSharedState(props.on);
    const computedProps = generateBindings(props, [
      'onIcon',
      'offIcon',
      'ariaLabel',
      'on',
    ]);

    function handleChange(e: any) {
      on.value = e.detail.isOn;
    }

    return html`
      <mwc-icon-button-toggle
        ${spread({ ...computedProps })}
        ?on="${on.value}"
        onIcon="${ifDefined(iconToAttr(props.onIcon))}"
        offIcon="${ifDefined(iconToAttr(props.offIcon))}"
        @icon-button-toggle-change="${handleChange}"
        aria-label="${props.ariaLabel}"
      >
        ${iconToSlotted(props.onIcon)} ${iconToSlotted(props.offIcon)}
        ${props.content.map(el => (el instanceof View ? el.body : el))}
      </mwc-icon-button-toggle>
    `;
  },
});

function interpretIcon(
  slot: string,
  icon: View | ((slot: string) => TemplateResult | SVGTemplateResult)
) {
  if (typeof icon === 'function') {
    return icon(slot);
  }
  return icon.slot(slot);
}

export function OnIcon(
  icon: View | ((slot: string) => TemplateResult | SVGTemplateResult)
) {
  return interpretIcon('onIcon', icon);
}

export function OffIcon(
  icon: View | ((slot: string) => TemplateResult | SVGTemplateResult)
) {
  return interpretIcon('offIcon', icon);
}
